import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft, FiUser, FiUsers, FiLink, FiTag, FiCopy } from "react-icons/fi";
import { creatorGetProfile } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import CreatorSignupDetails from "../../component/MainLayout/CreatorSignupDetails";
import style from "./CreatorProfile.module.css";

// Read-only view of the creator's own profile. The hero header (banner,
// avatar, name, channel stat chips) is specific to this page; everything
// else (the full signup submission — license terms, revenue share, payment
// preferences, restrictions, documents — plus the licensing agreement
// preview/sign flow) reuses CreatorSignupDetails, the same component the
// pending-review screen shows, so a creator can see that detail here too
// instead of only while their application is still under review.
export default function CreatorProfile() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(() => {
    return creatorGetProfile()
      .then((res) => {
        setData(res?.data?.data || null);
      })
      .catch((error) => {
        showToast(
          error?.response?.data?.message || "Couldn't load your profile",
          "error"
        );
      });
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchProfile().finally(() => setLoading(false));
  }, [fetchProfile]);

  const handleCopyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(data.referral_link);
      showToast("Referral link copied!", "success");
    } catch {
      showToast("Could not copy link.", "error");
    }
  };

  const channel = data?.channel || {};
  const hasStats = channel.channel_subscribers || channel.channel_category || data?.referral_link;

  return (
    <div className={style.page}>
      <div className={style.headerRow}>
        <button className={style.backBtn} onClick={() => navigate(-1)}>
          <FiArrowLeft /> Back
        </button>
      </div>

      {loading ? (
        <div className={style.loading}>Loading…</div>
      ) : !data ? (
        <div className={style.loading}>Couldn't load your profile.</div>
      ) : (
        <>
          <div className={style.hero}>
            <div
              className={style.cover}
              style={
                channel.channel_banner
                  ? {
                      // Setting inline backgroundImage replaces the whole
                      // layered value the CSS class sets (inline always wins
                      // over the class for that sub-property), so the
                      // contrast-guaranteeing fade has to be re-included here
                      // too, not just left to the class.
                      backgroundImage: `linear-gradient(to bottom, transparent 55%, rgba(0, 0, 0, 0.65) 100%), url(${channel.channel_banner})`,
                    }
                  : undefined
              }
            />

            <div className={style.heroBody}>
              <div className={style.avatarRing}>
                {data.profile_img ? (
                  <img src={data.profile_img} alt="Profile" className={style.avatar} />
                ) : (
                  <span className={style.avatarPlaceholder}>
                    <FiUser />
                  </span>
                )}
              </div>

              <div className={style.identity}>
                <h1 className={style.name}>{data.name}</h1>
                <div className={style.badgeRow}>
                  <span className={style.roleBadge}>{data.role || "Creator"}</span>
                  {channel.channel_name && (
                    <span className={style.channelName}>{channel.channel_name}</span>
                  )}
                </div>
              </div>
            </div>

            {hasStats && (
              <div className={style.chipRow}>
                {channel.channel_category && (
                  <span className={style.chip}>
                    <FiTag /> {channel.channel_category}
                  </span>
                )}
                {channel.channel_subscribers > 0 && (
                  <span className={style.chip}>
                    <FiUsers /> {Number(channel.channel_subscribers).toLocaleString()} subscribers
                  </span>
                )}
                {data.referral_link && (
                  <button
                    type="button"
                    className={`${style.chip} ${style.chipLink}`}
                    onClick={handleCopyReferralLink}
                    title="Copy referral link"
                  >
                    <FiLink /> <span className={style.chipLinkText}>{data.referral_link}</span>
                    <FiCopy className={style.chipCopyIcon} />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Full signup submission — details, license terms, revenue share,
              payment preferences, restrictions, documents — plus the
              agreement preview/sign flow. Same component the under-review
              screen uses, fed the same profile shape. */}
          <div className={style.signupDetails}>
            <CreatorSignupDetails profile={data} onRefresh={fetchProfile} />
          </div>
        </>
      )}
    </div>
  );
}
